import type { ColumnDefine, ListTableConstructorOptions, ColumnsDefine } from '@visactor/vtable';
import { ListTable, TABLE_EVENT_TYPE } from '@visactor/vtable';
import { isValid } from '@visactor/vutils';
import type {
  IWorkSheetOptions,
  IWorkSheetAPI,
  CellCoord,
  CellRange,
  CellValue,
  IFormulaManagerOptions,
  IThemeDefine,
  IFilterConfig,
  IFilterState,
  SheetData,
  IVTableSheetUpdateOptions
} from '../ts-types';
import type { TYPES, VTableSheet } from '..';
import { isPropertyWritable } from '../tools';
import { VTableThemes } from '../ts-types';
import { FormulaPasteProcessor } from '../formula/formula-paste-processor';
import { WorkSheetEventManager } from '../event/worksheet-event-manager';
import type { VTableSheetEventBus } from '../event/vtable-sheet-event-bus';
import type { IWorksheetEventSource } from '../event/event-interfaces';

/**
 * Sheet constructor options. 内部类型Sheet的构造函数参数类型
 */
export type WorkSheetConstructorOptions = {
  /** 表格数据 */
  data?: SheetData;
  /** 公式计算选项 */
  formula?: IFormulaManagerOptions;
  /** Sheet 唯一标识 */
  sheetKey: string;
  /** Sheet 标题 */
  sheetTitle: string;
} & Omit<ListTableConstructorOptions, 'records'>;

/**
 * WorkSheet 增量更新配置项
 *
 * 仅涵盖与布局和交互相关的常用配置，不包含 records/columns 等结构性配置。
 */
type WorkSheetUpdateOptions = Pick<
  IVTableSheetUpdateOptions,
  'defaultRowHeight' | 'defaultColWidth' | 'dragOrder' | 'VTablePluginModules'
> & {
  theme?: TYPES.VTableThemes.ITableThemeDefine;
};

type WorkSheetColumn = IWorkSheetOptions['columns'][number] & {
  columns?: IWorkSheetOptions['columns'];
  children?: IWorkSheetOptions['columns'];
};

const getChildColumns = (column: IWorkSheetOptions['columns'][number]): IWorkSheetOptions['columns'] | undefined =>
  (column as WorkSheetColumn).columns ?? (column as WorkSheetColumn).children;

const normalizeColumnsField = (columns: IWorkSheetOptions['columns'], startFieldIndex = 0): number => {
  if (!columns?.length) {
    return startFieldIndex;
  }

  let fieldIndex = startFieldIndex;

  columns.forEach(column => {
    const childColumns = getChildColumns(column);

    if (childColumns?.length) {
      fieldIndex = normalizeColumnsField(childColumns, fieldIndex);
      return;
    }

    if (!isValid(column.field)) {
      column.field = fieldIndex;
    }
    if (!isValid(column.key)) {
      column.key = column.field as any;
    }
    fieldIndex++;
  });

  return fieldIndex;
};

const collectLeafColumns = (columns: IWorkSheetOptions['columns'] = []): IWorkSheetOptions['columns'] => {
  const leafColumns: IWorkSheetOptions['columns'] = [];

  columns.forEach(column => {
    const childColumns = getChildColumns(column);
    if (childColumns?.length) {
      leafColumns.push(...collectLeafColumns(childColumns));
      return;
    }
    leafColumns.push(column);
  });

  return leafColumns;
};

const hasObjectFieldColumn = (columns: IWorkSheetOptions['columns'] = []): boolean =>
  columns.some(column => {
    const childColumns = getChildColumns(column);
    if (childColumns?.length) {
      return hasObjectFieldColumn(childColumns);
    }
    return isValid(column.field) && typeof column.field !== 'number';
  });

const getAddRecordRule = (options: IWorkSheetOptions): ListTableConstructorOptions['addRecordRule'] => {
  if (options.addRecordRule) {
    return options.addRecordRule;
  }

  const hasObjectRecord = options.data?.some(record => record && typeof record === 'object' && !Array.isArray(record));
  return hasObjectRecord || hasObjectFieldColumn(options.columns) ? 'Object' : 'Array';
};

const getValueByField = (record: Record<string, any>, field: any): any => {
  if (Array.isArray(field)) {
    return field.reduce((value, key) => value?.[key], record);
  }
  if (typeof field === 'string') {
    if (Object.prototype.hasOwnProperty.call(record, field)) {
      return record[field];
    }
    return field.split('.').reduce((value, key) => value?.[key], record);
  }
  return record[field];
};

const setValueByField = (record: Record<string, any>, field: any, value: any): void => {
  if (Array.isArray(field)) {
    let target = record;
    field.forEach((key, index) => {
      if (index === field.length - 1) {
        target[key] = value;
        return;
      }
      if (typeof target[key] !== 'object' || target[key] === null) {
        target[key] = {};
      }
      target = target[key];
    });
    return;
  }

  if (typeof field === 'string' && !Object.prototype.hasOwnProperty.call(record, field) && field.includes('.')) {
    const keys = field.split('.');
    let target = record;
    keys.forEach((key, index) => {
      if (index === keys.length - 1) {
        target[key] = value;
        return;
      }
      if (typeof target[key] !== 'object' || target[key] === null) {
        target[key] = {};
      }
      target = target[key];
    });
    return;
  }

  record[field] = value;
};

export class WorkSheet implements IWorkSheetAPI, IWorksheetEventSource {
  /** 选项 */
  options: IWorkSheetOptions;
  /** 容器 */
  container: HTMLElement;
  /** 表格实例 */
  tableInstance?: ListTable;
  /** 元素 */
  element: HTMLElement;
  /** 选择范围 */
  private selection: CellRange | null = null;
  /** Sheet 唯一标识 */
  private _sheetKey: string;
  /** Sheet 标题 */
  private _sheetTitle: string;

  /** 事件总线 */
  private eventBus: VTableSheetEventBus;

  /** WorkSheet 事件管理器 */
  eventManager: WorkSheetEventManager;

  private vtableSheet: VTableSheet;

  editingCell: { sheet: string; row: number; col: number } | null = null;

  /**
   * 获取 Sheet Key
   */
  get sheetKey(): string {
    return this._sheetKey;
  }

  /**
   * 获取事件总线
   */
  getEventBus(): VTableSheetEventBus {
    if (!this.eventBus) {
      // If eventBus is not initialized yet, return the parent VTableSheet's event bus
      return this.vtableSheet.getEventBus();
    }
    return this.eventBus;
  }

  /**
   * 获取 Sheet 标题
   */
  get sheetTitle(): string {
    return this._sheetTitle;
  }

  /**
   * 设置 Sheet 标题
   */
  set sheetTitle(title: string) {
    this._sheetTitle = title;
  }

  constructor(sheet: VTableSheet, options: IWorkSheetOptions) {
    this.options = options;
    this.container = options.container;

    // 初始化基本属性
    this._sheetKey = options.sheetKey;
    this._sheetTitle = options.sheetTitle;
    this.vtableSheet = sheet;

    // 创建表格元素
    this.element = this._createRootElement();
    this.container.appendChild(this.element);

    // 初始化表格
    this._initializeTable();

    // 设置事件监听
    this._setupEventListeners();
  }

  /**
   * 获取行数
   */
  get rowCount(): number {
    return this.getRowCount();
  }

  /**
   * 获取列数
   */
  get colCount(): number {
    return this.getColumnCount();
  }

  /**
   * 获取行数
   */
  getRowCount(): number {
    return this.tableInstance.rowCount;
  }

  /**
   * 获取列数
   */
  getColumnCount(): number {
    return this.tableInstance.colCount;
  }

  /**
   * 获取表格数据
   */
  get records(): any {
    return this.getData();
  }

  /**
   * 获取列定义
   */
  get columns(): any {
    return this.options.columns || [];
  }

  /**
   * 创建根元素
   */
  private _createRootElement(): HTMLElement {
    const element = document.createElement('div');
    element.setAttribute('sheet-key', `${this.sheetKey}`);
    element.classList.add('vtable-sheet');
    element.style.outline = 'none';
    element.style.position = 'relative';

    const width = this.container.clientWidth || 800;
    const height = this.container.clientHeight || 600;

    element.style.width = `${width}px`;
    element.style.height = `${height}px`;

    return element;
  }

  /**
   * 初始化表格实例
   */
  private _initializeTable(): void {
    // 这里应该是实际的表格初始化逻辑
    const tableOptions = this._generateTableOptions();
    this.tableInstance = new ListTable(tableOptions);
    this._bindKeyboardSelectionVisibility();
    this.element.classList.add('vtable-excel-cursor');
    // 使用统一事件总线
    this.eventBus = this.vtableSheet.getEventBus();

    // 初始化 WorkSheet 事件管理器
    this.eventManager = new WorkSheetEventManager(this);
    // 在 tableInstance 上设置 VTableSheet 引用，方便插件访问
    (this.tableInstance as any).__vtableSheet = this.vtableSheet;

    // 通知 VTableSheet 的事件中转器绑定这个 sheet 的事件
    (this.vtableSheet as any).tableEventRelay.bindSheetEvents(this.sheetKey, this.tableInstance);

    // 触发工作表准备就绪事件
    if (this.eventManager) {
      // this.eventManager.emitReady();
      // 触发数据加载完成事件
      this.eventManager.emitDataLoaded(this.rowCount, this.colCount);
    }
  }

  private _bindKeyboardSelectionVisibility(): void {
    let isForcingKeyboardSelectionVisible = false;
    let previousMakeSelectCellVisible: boolean | undefined;
    let restoreTimer: number | undefined;

    const isArrowKeyEvent = (event?: KeyboardEvent) =>
      !!event && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key);

    const restoreMakeSelectCellVisible = () => {
      const tableInstance = this.tableInstance;
      if (!tableInstance || !isForcingKeyboardSelectionVisible) {
        return;
      }

      if (tableInstance.options.select) {
        tableInstance.options.select.makeSelectCellVisible = previousMakeSelectCellVisible;
      }
      isForcingKeyboardSelectionVisible = false;
      previousMakeSelectCellVisible = undefined;
    };

    this.tableInstance?.on(TABLE_EVENT_TYPE.BEFORE_KEYDOWN, ({ event }: { event?: KeyboardEvent }) => {
      if (!isArrowKeyEvent(event)) {
        return;
      }

      const tableInstance = this.tableInstance;
      if (!tableInstance) {
        return;
      }

      tableInstance.options.select ??= {};
      previousMakeSelectCellVisible = tableInstance.options.select.makeSelectCellVisible;
      tableInstance.options.select.makeSelectCellVisible = true;
      isForcingKeyboardSelectionVisible = true;

      if (restoreTimer !== undefined) {
        window.clearTimeout(restoreTimer);
      }
      restoreTimer = window.setTimeout(restoreMakeSelectCellVisible, 0);
    });

    this.tableInstance?.on(TABLE_EVENT_TYPE.KEYDOWN, ({ event }: { event?: KeyboardEvent }) => {
      if (isArrowKeyEvent(event)) {
        restoreMakeSelectCellVisible();
      }
    });
  }

  /**
   * 生成VTable选项
   */
  private _generateTableOptions(): ListTableConstructorOptions {
    let isShowTableHeader = this.options.showHeader;
    // 转换为ListTable的选项
    if (!this.options.columns) {
      isShowTableHeader = isValid(isShowTableHeader) ? isShowTableHeader : false;
      this.options.columns = [];
    } else {
      normalizeColumnsField(this.options.columns);
    }
    if (!this.options.data) {
      this.options.data = [];
    } else if (this.options.columns.length === 0 && this.options.firstRowAsHeader) {
      const data = this.options.data;
      if (data && data.length > 0) {
        for (let i = 0; i < data[0].length; i++) {
          this.options.columns[i] = {
            field: i,
            key: i as any,
            title: data[0][i],
            filter: !!this.options.filter
          };
        }
      }
      data.shift();
      isShowTableHeader = true;
    }

    const keyboardOptions = {
      ...this.options.keyboardOptions,
      copySelected: true,
      getCopyCellValue: {
        html: this.getCellValueConsiderFormula.bind(this)
      },
      processFormulaBeforePaste: this.processFormulaPaste.bind(this),
      pasteValueToCell: true,
      showCopyCellBorder: true,
      cutSelected: true
    };
    const addRecordRule = getAddRecordRule(this.options);
    return {
      ...(this.options as any),
      dragOrder: {
        maintainArrayDataOrder: true
      },
      addRecordRule,
      syncRecordOperationsToSourceRecords: true,
      defaultCursor: 'cell',
      records: this.options.data,
      sortState: this.options.sortState,
      container: this.element,
      showHeader: isShowTableHeader,
      keyboardOptions,
      theme: this._adjustTheme(this.options.theme),
      excelOptions: {
        fillHandle: true
      },
      customConfig: {
        selectCellWhenCellEditorNotExists: true
      }
      // maintainedColumnCount: 120
      // 其他特定配置
    };
  }

  _adjustTheme(theme: TYPES.VTableThemes.ITableThemeDefine): TYPES.VTableThemes.ITableThemeDefine {
    //更改theme 的frameStyle
    let changedTheme: TYPES.VTableThemes.ITableThemeDefine;
    if (!theme) {
      this.options.theme = VTableThemes.DEFAULT;
    } else {
      this.options.theme = theme;
    }
    if (this.options.theme.bodyStyle && !isPropertyWritable(this.options.theme, 'bodyStyle')) {
      //测试是否使用了主题 使用了主题配置项不可写。
      changedTheme = (this.options.theme as TYPES.VTableThemes.TableTheme).extends(
        (this.options.theme as TYPES.VTableThemes.TableTheme).getExtendTheme()
      ); //防止将原主题如DARK ARCO的属性改掉
      const extendThemeOption = (changedTheme as TYPES.VTableThemes.TableTheme).getExtendTheme();

      extendThemeOption.frameStyle = Object.assign({}, extendThemeOption.frameStyle, {
        shadowBlur: 0,
        cornerRadius: 0,
        borderLineWidth: 0
      });
    } else {
      // 确保theme是一个对象而不是字符串
      if (typeof this.options.theme === 'string') {
        console.warn('theme is a string, it will be ignored');
      } else {
        changedTheme = this.options.theme;
        changedTheme.frameStyle = Object.assign({}, this.options.theme.frameStyle, {
          shadowBlur: 0,
          cornerRadius: 0,
          borderLineWidth: 0
        });
      }
    }
    return changedTheme;
  }
  /**
   * 设置事件监听
   */
  private _setupEventListeners(): void {
    if (this.tableInstance) {
      // 监听单元格选择事件 - 优化：移除console.log调试代码
      this.tableInstance.on('mousedown_cell', (event: any) => {
        if (this.vtableSheet.formulaManager.formulaWorkingOnCell) {
          //防止公式输入状态下，原本的input元素blur掉，导致公式输入框无法输入
          event.event.preventDefault();
        }
      });

      // 监听选择变化事件（多选时）
      this.tableInstance.on('selected_changed' as any, (event: any) => {
        // 判断是drag过程中的选中单元格变化
        if (!this.tableInstance.eventManager.isDraging) {
          if (!this.vtableSheet.formulaManager.formulaWorkingOnCell) {
            this.editingCell = {
              sheet: this.getKey(),
              row: event.row,
              col: event.col
            };
          }
          this.handleCellSelected(event);
        }
        this.handleSelectionChanged(event);
      });

      // 监听拖拽选择结束事件 - 优化：移除console.log和debugger调试代码
      this.tableInstance.on('drag_select_end' as any, (event: any) => {
        this.handleDragSelectEnd(event);
      });

      // 监听单元格值变更事件
      this.tableInstance.on('change_cell_value', (event: any) => {
        this.handleCellValueChanged(event);
      });

      // 监听数据记录变更事件 - 用于调整公式引用
      // 注意：'add_record' 事件类型需要使用 as any 绕过类型检查
      (this.tableInstance as any).on('add_record', (event: any) => {
        this.handleDataRecordsChanged('add', event);
      });

      // 注意：'delete_record' 事件类型需要使用 as any 绕过类型检查
      (this.tableInstance as any).on('delete_record', (event: any) => {
        this.handleDataRecordsChanged('delete', event);
      });
      // 注意：'add_column' 事件类型尚未在 VTable 中定义，这里使用 as any 绕过类型检查
      (this.tableInstance as any).on('add_column', (event: any) => {
        this.handleColumnsChanged('add', event);
      });

      // 注意：'delete_column' 事件类型需要使用 as any 绕过类型检查
      (this.tableInstance as any).on('delete_column', (event: any) => {
        this.handleColumnsChanged('delete', event);
      });

      // 监听编辑结束事件，恢复十字光标
      this.tableInstance.on('click_cell', () => {
        this.element.classList.add('vtable-excel-cursor');
      });
      // 监听编辑结束事件，恢复十字光标
      this.tableInstance.on('change_header_position', (event: any) => {
        this.handleChangeHeaderPosition(event);
      });
    }
  }

  /**
   * 处理单元格选择事件
   * @param event 选择事件
   */
  private handleCellSelected(event: any): void {
    // 更新选择范围
    this.selection = {
      startRow: event.row,
      startCol: event.col,
      endRow: event.row,
      endCol: event.col
    };
    // 如果在公式编辑状态，不处理
    if (this.vtableSheet.formulaManager.formulaWorkingOnCell) {
      return;
    }

    // 重置公式栏显示标志，让公式栏显示选中单元格的值
    const formulaUIManager = this.vtableSheet.formulaUIManager;
    formulaUIManager.isFormulaBarShowingResult = false;
    formulaUIManager.clearFormula();
    formulaUIManager.updateFormulaBar();
  }

  /**
   * 处理选择变化事件
   * @param event 选择变化事件
   */
  private handleSelectionChanged(event: any): void {
    if (event?.ranges?.length) {
      const r = event.ranges[event.ranges.length - 1];
      this.selection = {
        startRow: r.start.row,
        startCol: r.start.col,
        endRow: r.end.row,
        endCol: r.end.col
      };
    }
    this.vtableSheet.formulaManager.formulaRangeSelector.handleSelectionChangedForRangeMode();
  }

  /**
   * 处理拖拽选择结束事件
   * @param event 拖拽选择结束事件
   */
  private handleDragSelectEnd(event: any): void {
    if (event?.cells?.length) {
      const first = event.cells[0][0];
      const lastRow = event.cells[event.cells.length - 1];
      const last = lastRow[lastRow.length - 1];
      this.selection = {
        startRow: first.row,
        startCol: first.col,
        endRow: last.row,
        endCol: last.col
      };
    }
    this.vtableSheet.formulaManager.formulaRangeSelector.handleSelectionChangedForRangeMode();
  }

  /**
   * 处理单元格值变更事件
   * @param event 值变更事件
   */
  private handleCellValueChanged(event: any): void {
    this.vtableSheet.formulaManager.formulaRangeSelector.handleCellValueChanged(event);
  }

  /**
   * 处理数据记录变更事件 - 用于调整公式引用
   * @param type 变更类型 ('add' | 'delete')
   * @param event 数据变更事件
   */
  private handleDataRecordsChanged(type: 'add' | 'delete', event: any): void {
    try {
      const sheetKey = this.getKey();
      //#region 处理数据变化后，公式引擎中的数据也需要更新
      const normalizedData = this.vtableSheet.formulaManager.normalizeSheetData(
        this.tableInstance.records,
        this.tableInstance
      );
      this.vtableSheet.formulaManager.formulaEngine.updateSheetData(sheetKey, normalizedData);
      //#endregion
      if (type === 'add') {
        // 处理添加记录事件
        const { recordIndex, recordCount } = event;
        if (recordIndex !== undefined && recordCount > 0) {
          // 在指定位置插入行，需要调整该位置之后的公式引用
          this.vtableSheet.formulaManager.addRows(
            sheetKey,
            recordIndex + this.tableInstance.columnHeaderLevelCount,
            recordCount
          );
        } else {
          // 默认在末尾添加
          const currentRowCount = this.getRowCount();
          this.vtableSheet.formulaManager.addRows(sheetKey, currentRowCount, recordCount);
        }
      } else if (type === 'delete') {
        // 处理删除记录事件
        const { rowIndexs, deletedCount } = event;
        if (rowIndexs && rowIndexs.length > 0) {
          // 为了简化，我们假设删除的是连续的行，从最小的索引开始
          const minIndex = Math.min(...rowIndexs.flat());
          this.vtableSheet.formulaManager.removeRows(sheetKey, minIndex, deletedCount);
        }
      }
      // update 事件不需要调整引用，因为只是数据内容变更
    } catch (error) {
      console.error(`Failed to handle data records changed (${type}):`, error);
    }
  }
  /**
   * 处理列变更事件 - 用于调整公式引用
   * @param type 变更类型 ('add' | 'delete')
   * @param event 列变更事件
   */
  private handleColumnsChanged(type: 'add' | 'delete', event: any): void {
    try {
      const sheetKey = this.getKey();
      //#region 处理数据变化后，公式引擎中的数据也需要更新
      const normalizedData = this.vtableSheet.formulaManager.normalizeSheetData(
        this.tableInstance.records,
        this.tableInstance
      );
      this.vtableSheet.formulaManager.formulaEngine.updateSheetData(sheetKey, normalizedData);
      //#endregion
      if (type === 'add') {
        // 处理添加列事件
        const { columnIndex, columnCount } = event;
        if (columnIndex !== undefined && columnCount > 0) {
          // 在指定位置插入列，需要调整该位置之后的公式引用
          this.vtableSheet.formulaManager.addColumns(sheetKey, columnIndex, columnCount);
        } else {
          // 默认在末尾添加
          const currentColumnCount = this.getColumnCount();
          this.vtableSheet.formulaManager.addColumns(sheetKey, currentColumnCount, columnCount);
        }
      } else if (type === 'delete') {
        // 处理删除列事件
        const { deleteColIndexs } = event;
        if (deleteColIndexs && deleteColIndexs.length > 0) {
          // 为了简化，我们假设删除的是连续的列，从最小的索引开始
          const minIndex = Math.min(...deleteColIndexs.flat());
          const deletedCount = deleteColIndexs.length;
          this.vtableSheet.formulaManager.removeColumns(sheetKey, minIndex, deletedCount);
        }
      }
      // update 事件不需要调整引用，因为只是数据内容变更
    } catch (error) {
      console.error(`Failed to handle columns changed (${type}):`, error);
    }
  }
  /**
   * 处理表头位置变更事件
   * @param event 表头位置变更事件
   */
  private handleChangeHeaderPosition(event: any): void {
    console.log('handleChangeHeaderPosition', event);
    if (event.movingColumnOrRow === 'column') {
      this.handleChangeColumnHeaderPosition(event);
    } else {
      this.handleChangeRowHeaderPosition(event);
    }
  }
  /**
   * 处理列表头位置变更事件
   * @param event 列表头位置变更事件
   */
  private handleChangeColumnHeaderPosition(event: any): void {
    console.log('handleChangeColumnHeaderPosition', event);
    // 注意：tableInstance.options.columns 中的顺序并未更新（和其他操作如delete/add等操作不同）需要注意后续是否有什么问题
    const { source, target } = event;
    const { col: sourceCol } = source;
    const { col: targetCol } = target;
    const sheetKey = this.getKey();
    //#region 处理数据变化后，公式引擎中的数据也需要更新
    const normalizedData = this.vtableSheet.formulaManager.normalizeSheetData(
      this.tableInstance.records,
      this.tableInstance
    );
    this.vtableSheet.formulaManager.formulaEngine.updateSheetData(sheetKey, normalizedData);
    //#endregion
    // 在指定位置插入列，需要调整该位置之后的公式引用
    this.vtableSheet.formulaManager.changeColumnHeaderPosition(sheetKey, sourceCol, targetCol);
  }
  /**
   * 处理行表头位置变更事件
   * @param event 行表头位置变更事件
   */
  handleChangeRowHeaderPosition(event: any): void {
    // 注意：tableInstance.options.columns 中的顺序并未更新（和其他操作如delete/add等操作不同）需要注意后续是否有什么问题
    const { source, target } = event;
    const { row: sourceRow } = source;
    const { row: targetRow } = target;
    const sheetKey = this.getKey();
    //#region 处理数据变化后，公式引擎中的数据也需要更新
    const normalizedData = this.vtableSheet.formulaManager.normalizeSheetData(
      this.tableInstance.records,
      this.tableInstance
    );
    this.vtableSheet.formulaManager.formulaEngine.updateSheetData(sheetKey, normalizedData);
    //#endregion
    // 在指定位置插入行，需要调整该位置之后的公式引用
    this.vtableSheet.formulaManager.changeRowHeaderPosition(sheetKey, sourceRow, targetRow);
  }

  // 用于防止短时间内多次调用resize的节流变量
  private resizeTimer: number | null = null;
  private isResizing = false;

  /**
   * 更新Sheet大小
   * 使用节流方式避免频繁调用resize
   */
  resize(): void {
    if (!this.element) {
      return;
    }

    // 如果正在进行调整，清除之前的定时器
    if (this.resizeTimer !== null) {
      window.clearTimeout(this.resizeTimer);
      this.resizeTimer = null;
    }

    // 如果已经在执行resize，设置一个短期延迟
    if (this.isResizing) {
      this.resizeTimer = window.setTimeout(() => {
        this.doResize();
        this.resizeTimer = null;
      }, 50);
      return;
    }

    // 否则直接执行resize
    this.doResize();
  }

  /**
   * 实际执行调整大小的操作
   * @private
   */
  private doResize(): void {
    try {
      this.isResizing = true;

      const width = this.container.clientWidth || 800;
      const height = this.container.clientHeight || 600;

      // 只有尺寸确实变化时才更新样式和触发表格实例的resize
      if (parseInt(this.element.style.width, 10) !== width || parseInt(this.element.style.height, 10) !== height) {
        this.element.style.width = `${width}px`;
        this.element.style.height = `${height}px`;

        // 更新表格实例大小
        if (this.tableInstance) {
          // 触发VTable的resize
          this.tableInstance.resize();
        }

        // // 触发工作表尺寸改变事件
        // if (this.eventManager) {
        //   this.eventManager.emitResized(width, height);
        // }
      }
    } catch (error) {
      console.error('Error during resize:', error);
    } finally {
      this.isResizing = false;
    }
  }

  /**
   * 获取Sheet元素
   */
  getElement(): HTMLElement {
    return this.element;
  }

  /**
   * 获取Sheet容器
   */
  getContainer(): HTMLElement {
    return this.container;
  }

  /**
   * 获取Sheet的key
   */
  getKey(): string {
    return this.sheetKey;
  }

  /**
   * 获取Sheet的标题
   */
  getTitle(): string {
    return this.sheetTitle;
  }

  /**
   * 设置Sheet的标题
   */
  setTitle(title: string): void {
    this.sheetTitle = title;
    this.options.sheetTitle = title;
  }

  /**
   * 获取列定义
   */
  getColumns(): ColumnDefine[] {
    return this.tableInstance.columns || [];
  }

  /**
   * 获取表格数据
   */
  getData(): SheetData {
    // 从表格实例获取数据
    return this.options.data || [];
  }

  private getLeafColumns(): IWorkSheetOptions['columns'] {
    return collectLeafColumns(this.options.columns);
  }

  private getDataCellByTableCell(col: number, row: number): { col: number; row: number } | null {
    const bodyIndex = this.tableInstance?.getBodyIndexByTableIndex?.(col, row) ?? { col, row };
    if (bodyIndex.col < 0 || bodyIndex.row < 0) {
      return null;
    }
    return bodyIndex;
  }

  private getDataCellValue(col: number, row: number): any {
    const rowData = this.getData()[row];
    if (Array.isArray(rowData)) {
      return rowData[col];
    }
    if (rowData && typeof rowData === 'object') {
      const field = this.getLeafColumns()[col]?.field;
      if (isValid(field)) {
        return getValueByField(rowData as Record<string, any>, field);
      }
    }
    return undefined;
  }

  private getDataColumnCount(rowData: any): number {
    return Array.isArray(rowData) ? rowData.length : this.getLeafColumns().length;
  }

  private setDataCellValue(col: number, row: number, value: any, tableCol?: number, tableRow?: number): void {
    const rowData = this.getData()[row];
    if (Array.isArray(rowData)) {
      rowData[col] = value;
    } else if (rowData && typeof rowData === 'object') {
      const field = this.getLeafColumns()[col]?.field;
      if (isValid(field)) {
        setValueByField(rowData as Record<string, any>, field, value);
      }
    }

    if (this.tableInstance) {
      const tableIndex =
        isValid(tableCol) && isValid(tableRow)
          ? { col: tableCol, row: tableRow }
          : this.tableInstance.getTableIndexByBodyIndex(col, row);
      this.tableInstance.changeCellValue(tableIndex.col, tableIndex.row, value);
    }
  }

  getCopiedData(): any[][] {
    // 为了避免影响当前数据，所以需要复制一份数据
    const leafColumns = this.getLeafColumns();
    return this.getData().map(row =>
      Array.isArray(row)
        ? row.slice()
        : leafColumns.map(column =>
            row && typeof row === 'object' ? getValueByField(row as Record<string, any>, column.field) : undefined
          )
    );
  }
  /**
   * 获取指定坐标的单元格值
   * @param col 列索引
   * @param row 行索引
   */
  getCellValue(col: number, row: number): any {
    if (this.tableInstance) {
      try {
        const value = this.tableInstance.getCellValue(col, row);
        return value;
      } catch (error) {
        console.warn('Failed to get cell value from VTable:', error);
      }
    }

    const rowData = this.getData()[row];
    if (Array.isArray(rowData) && rowData[col] !== undefined) {
      return rowData[col];
    }
    const dataCell = this.getDataCellByTableCell(col, row);
    return dataCell ? this.getDataCellValue(dataCell.col, dataCell.row) ?? null : null;
  }
  /**
   * 获取指定坐标的单元格值
   * @param col 列索引
   * @param row 行索引
   */
  getCellValueConsiderFormula(col: number, row: number): any {
    if (this.tableInstance) {
      try {
        if (
          this.vtableSheet.formulaManager.isCellFormula({
            sheet: this.getKey(),
            row,
            col
          })
        ) {
          return this.vtableSheet.formulaManager.getCellFormula({
            sheet: this.getKey(),
            row,
            col
          });
        }
        return this.getCellValue(col, row);
      } catch (error) {
        console.warn('Failed to get cell value from VTable:', error);
      }
      return null;
    }
  }

  /**
   * 设置指定坐标的单元格值
   * @param col 列索引
   * @param row 行索引
   * @param value 新值
   */
  setCellValue(col: number, row: number, value: any): void {
    const dataCell = this.getDataCellByTableCell(col, row);
    if (dataCell) {
      this.setDataCellValue(dataCell.col, dataCell.row, value, col, row);
    }
  }

  /**
   * 根据A1地址获取单元格信息
   * @param address A1地址
   */
  getCellByAddress(address: string): { coord: CellCoord; value: CellValue } {
    const coord = this.coordFromAddress(address);
    return {
      coord,
      value: this.getCellValue(coord.col, coord.row)
    };
  }

  /**
   * 将行/列坐标转换为A1格式
   * @param coord 坐标
   */
  addressFromCoord(coord: CellCoord): string;
  addressFromCoord(col: number, row: number): string;
  addressFromCoord(coordOrCol: CellCoord | number, row?: number): string {
    let col: number;
    let rowNum: number;

    if (typeof coordOrCol === 'object') {
      col = coordOrCol.col;
      rowNum = coordOrCol.row;
    } else {
      col = coordOrCol;
      if (row === undefined) {
        throw new Error('row is required when coordOrCol is a number');
      }
      rowNum = row;
    }

    let colStr = '';
    let tempCol = col + 1;

    do {
      tempCol -= 1;
      colStr = String.fromCharCode(65 + (tempCol % 26)) + colStr;
      tempCol = Math.floor(tempCol / 26);
    } while (tempCol > 0);

    return `${colStr}${rowNum + 1}`;
  }

  /**
   * 将A1格式转换为行/列坐标
   * @param address A1地址
   */
  coordFromAddress(address: string): CellCoord {
    const match = address.match(/^([A-Z]+)([0-9]+)$/);
    if (!match) {
      throw new Error(`Invalid cell address: ${address}`);
    }

    const colStr = match[1];
    const rowStr = match[2];

    let col = 0;
    for (let i = 0; i < colStr.length; i++) {
      col = col * 26 + (colStr.charCodeAt(i) - 64);
    }

    return {
      row: parseInt(rowStr, 10) - 1,
      col: col - 1
    };
  }

  /**
   * 获取当前选择
   */
  getSelection(): CellRange | null {
    return this.selection;
  }

  /**
   * 获取多个选择范围（支持Ctrl/Cmd多选）
   */
  getMultipleSelections(): CellRange[] {
    if (!this.tableInstance) {
      return this.selection ? [this.selection] : [];
    }

    // 从底层VTable获取所有选择范围
    const vtableRanges = this.tableInstance.getSelectedCellRanges();

    if (vtableRanges.length === 0) {
      return this.selection ? [this.selection] : [];
    }

    // 转换VTable的坐标格式到WorkSheet格式
    return vtableRanges.map((range: any) => ({
      startRow: range.start.row,
      startCol: range.start.col,
      endRow: range.end.row,
      endCol: range.end.col
    }));
  }

  /**
   * 将第一行设置为表头
   */
  setFirstRowAsHeader(): void {
    const data = this.getData();
    if (data && data.length > 0) {
      for (let i = 0; i < data[0].length; i++) {
        if (!this.options.columns[i]) {
          this.options.columns[i] = {
            field: i,
            title: data[0][i]
          };
        } else {
          this.options.columns[i].title = data[0][i];
          this.options.columns[i].field = i;
        }
      }
    }
    data.shift();
    // Sheet 的 dragOrder 与 VTable ListTableConstructorOptions 的 dragOrder 类型不兼容，此处显式传入 VTable 所需格式
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- 仅用于从展开中排除，不传入 updateOption
    const { dragOrder: _omitSheetDragOrder, ...optionsRest } = this.options;
    this.tableInstance.updateOption({
      ...optionsRest,
      dragOrder: { maintainArrayDataOrder: true },
      columns: this.options.columns as any as ColumnsDefine,
      showHeader: true,
      records: data
    });
  }
  updateSheetOption(sheetOption: IWorkSheetOptions): void {
    this.options = {
      ...this.options,
      ...sheetOption
    };
    const tableOptions = this._generateTableOptions();
    this.tableInstance.updateOption(tableOptions);
  }
  // /**
  //  * 增量更新当前工作表配置，并映射到底层 VTable 的细粒度 API。
  //  *
  //  * 该方法只负责从全局配置提取需要更新到工作表的配置，不是sheets中的sheetDefine的配置更新的情况。
  //  */
  // updateGlobalOptionToSheet(option: WorkSheetUpdateOptions): void {
  //   if (!this.tableInstance || !option) {
  //     return;
  //   }

  //   const table = this.tableInstance;
  //   const nextOptionPatch: Partial<ListTableConstructorOptions> = {};
  //   // 主题（优先使用 updateTheme，而不是全量 updateOption）
  //   if (option.theme) {
  //     const tableTheme = option.theme;
  //     if (tableTheme) {
  //       this.options.theme = tableTheme;
  //       nextOptionPatch.theme = this._adjustTheme(tableTheme);
  //     }
  //   }

  //   // 默认行高/列宽（通过属性设置，并在最后触发一次重建渲染）
  //   if (option.defaultRowHeight !== undefined) {
  //     nextOptionPatch.defaultRowHeight = option.defaultRowHeight;
  //   }
  //   if (option.defaultColWidth !== undefined) {
  //     nextOptionPatch.defaultColWidth = option.defaultColWidth;
  //   }
  //   // 如果需要通过 updateOption 合并部分配置（如 showHeader / filter / theme 等）
  //   if (Object.keys(nextOptionPatch).length > 0) {
  //     const mergedOptions: ListTableConstructorOptions = {
  //       ...(table.options as ListTableConstructorOptions),
  //       ...nextOptionPatch
  //     };
  //     table.updateOption(mergedOptions, {
  //       // clearColWidthCache: false,
  //       // clearRowHeightCache: false
  //     });
  //   }
  // }

  /**
   * 处理公式粘贴 - 调整公式中的单元格引用
   * @param formulas 要粘贴的公式数组
   * @param sourceStartCol 源起始列
   * @param sourceStartRow 源起始行
   * @param targetStartCol 目标起始列
   * @param targetStartRow 目标起始行
   */
  processFormulaPaste(
    formulas: string[][],
    sourceStartCol: number,
    _sourceStartRow: number,
    targetStartCol: number,
    _targetStartRow: number
  ): string[][] {
    if (!formulas || formulas.length === 0) {
      return formulas;
    }

    // 计算整个范围的相对位移
    const colOffset = targetStartCol - sourceStartCol;
    const rowOffset = _targetStartRow - _sourceStartRow;

    // 使用计算出的位移来调整公式
    return FormulaPasteProcessor.adjustFormulasForPasteWithOffset(formulas, colOffset, rowOffset);
  }

  /**
   * 获取复制数据，包括公式处理
   */
  getCopyData(): (string | number)[][] {
    const selections = this.getMultipleSelections();
    if (selections.length === 0) {
      return [];
    }

    const result: string[][] = [];

    // 获取第一个选择范围
    const selection = selections[0];
    const rows = selection.endRow - selection.startRow + 1;
    const cols = selection.endCol - selection.startCol + 1;

    for (let row = 0; row < rows; row++) {
      const rowData: string[] = [];
      for (let col = 0; col < cols; col++) {
        const actualRow = selection.startRow + row;
        const actualCol = selection.startCol + col;
        const dataCell = this.getDataCellByTableCell(actualCol, actualRow);

        if (dataCell && this.getData()[dataCell.row]) {
          // 如果是公式，返回公式字符串；否则返回值
          if (
            this.vtableSheet.formulaManager.isCellFormula({
              sheet: this.getKey(),
              row: actualRow,
              col: actualCol
            })
          ) {
            const formula = this.vtableSheet.formulaManager.getCellFormula({
              sheet: this.getKey(),
              row: actualRow,
              col: actualCol
            });
            rowData.push(formula);
          } else {
            rowData.push(this.getDataCellValue(dataCell.col, dataCell.row) ?? '');
          }
        } else {
          rowData.push('');
        }
      }
      result.push(rowData);
    }

    return result;
  }

  /**
   * 粘贴数据，包括公式处理
   */
  pasteData(data: string[][], targetStartCol: number, targetStartRow: number): void {
    if (!data || data.length === 0) {
      return;
    }

    const selections = this.getMultipleSelections();
    if (selections.length === 0) {
      return;
    }

    // 获取源数据范围（从当前选择范围推断）
    const sourceSelection = selections[0];
    const sourceStartCol = sourceSelection.startCol;
    const sourceStartRow = sourceSelection.startRow;

    // 处理公式粘贴
    const processedData = this.processFormulaPaste(
      data,
      sourceStartCol,
      sourceStartRow,
      targetStartCol,
      targetStartRow
    );

    // 应用处理后的数据
    const dataArray = this.getData();
    for (let row = 0; row < processedData.length; row++) {
      for (let col = 0; col < processedData[row].length; col++) {
        const targetRow = targetStartRow + row;
        const targetCol = targetStartCol + col;
        const dataCell = this.getDataCellByTableCell(targetCol, targetRow);
        const rowData = dataCell ? dataArray[dataCell.row] : undefined;
        const columnCount = this.getDataColumnCount(rowData);

        if (rowData && dataCell && dataCell.col < columnCount) {
          const value = processedData[row][col];

          // 如果是公式，设置公式；否则设置普通值
          if (FormulaPasteProcessor.needsFormulaAdjustment(value)) {
            this.setCellFormula(targetRow, targetCol, value as string);
          } else {
            this.setDataCellValue(dataCell.col, dataCell.row, value, targetCol, targetRow);
          }
        }
      }
    }
  }

  /**
   * 设置单元格公式
   */
  setCellFormula(row: number, col: number, formula: string): void {
    if (this.vtableSheet.formulaManager) {
      this.vtableSheet.formulaManager.setCellContent(
        {
          sheet: this.getKey(),
          row,
          col
        },
        formula
      );

      // 事件触发移到 formula-manager 中处理，这里不再触发
      // 这样可以确保事件在正确的时机触发，并且只在操作成功时触发
    }
  }

  /**
   * 释放资源
   */
  release(): void {
    // 清理事件监听器
    if (this.tableInstance) {
      (this.vtableSheet as any).tableEventRelay.unbindSheetEvents(this.sheetKey, this.tableInstance);
    }

    // 释放表格实例
    if (this.tableInstance) {
      this.tableInstance.release();
    }

    // 从DOM中移除元素
    if (this.element && this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }

    // 清除引用
    this.tableInstance?.release();
  }
}
